import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { devtoolsConfig } from '../stores/secure-storage';
import type { FeatureFlag, RolloutStrategy } from './types';

interface FeatureFlagsState {
  // State
  flags: FeatureFlag[];
  isLoaded: boolean;
  lastFetchedAt: number | null;

  // Actions
  setFlags: (flags: FeatureFlag[]) => void;
  clearFlags: () => void;

  // Selectors
  getFlag: (key: string) => FeatureFlag | undefined;
  isEnabled: (key: string) => boolean;
  getFlagsByCategory: (categoryKey: string) => FeatureFlag[];
}

/**
 * Generate a deterministic hash for rollout percentage calculation
 * Uses the flag key + a stable identifier (could be user ID or session ID)
 */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Check if a flag is enabled based on rollout strategy
 */
function checkRollout(flag: FeatureFlag, identifier?: string): boolean {
  if (!flag.enabled) return false;

  if (flag.rolloutStrategy === 'all' as RolloutStrategy) {
    return true;
  }

  if (flag.rolloutStrategy === 'percentage' as RolloutStrategy) {
    // Use a stable identifier for consistent rollout
    const stableId = identifier || 'anonymous';
    const hash = hashCode(`${flag.key}-${stableId}`);
    const percentage = hash % 100;
    return percentage < flag.rolloutPercentage;
  }

  return flag.enabled;
}

// Get a stable identifier for rollout (from localStorage if available)
function getStableIdentifier(): string {
  const key = 'volovolo-rollout-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      flags: [],
      isLoaded: false,
      lastFetchedAt: null,

      // Actions
      setFlags: (flags) =>
        set(
          { flags, isLoaded: true, lastFetchedAt: Date.now() },
          false,
          'setFlags'
        ),

      clearFlags: () =>
        set(
          { flags: [], isLoaded: false, lastFetchedAt: null },
          false,
          'clearFlags'
        ),

      // Selectors
      getFlag: (key) => {
        return get().flags.find((f) => f.key === key);
      },

      isEnabled: (key) => {
        const flag = get().flags.find((f) => f.key === key);
        if (!flag) return false;
        return checkRollout(flag, getStableIdentifier());
      },

      getFlagsByCategory: (categoryKey) => {
        return get().flags.filter((f) => f.category?.key === categoryKey);
      },
    }),
    { ...devtoolsConfig, name: 'feature-flags-store' }
  )
);

/**
 * Selector hook for checking if a feature is enabled
 * Can be used outside of React components
 */
export function isFeatureEnabled(key: string): boolean {
  return useFeatureFlagsStore.getState().isEnabled(key);
}

/**
 * Get all loaded flags
 * Can be used outside of React components
 */
export function getAllFlags(): FeatureFlag[] {
  return useFeatureFlagsStore.getState().flags;
}
